import React, { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";

export default function EmailVerification() {
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    // Get email from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }

    // Start checking for verification with auto-reload
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Reload user to get latest emailVerified status
        await user.reload();
        
        if (user.emailVerified) {
          // Store user info
          localStorage.setItem("user", JSON.stringify({
            email: user.email,
            uid: user.uid
          }));
          
          // Redirect to home
          window.location.href = "/";
        }
      }
    });

    // Poll for verification status every 3 seconds
    const pollInterval = setInterval(async () => {
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        if (user.emailVerified) {
          localStorage.setItem("user", JSON.stringify({
            email: user.email,
            uid: user.uid
          }));
          window.location.href = "/";
        }
      }
    }, 3000);

    // Countdown for resend button
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      unsubscribe();
      clearInterval(pollInterval);
      clearInterval(timer);
    };
  }, []);

  const handleResendEmail = async () => {
    setResending(true);
    setResendSuccess(false);

    try {
      const user = auth.currentUser;
      if (user && !user.emailVerified) {
        const { sendEmailVerification } = await import("firebase/auth");
        await sendEmailVerification(user);
        setResendSuccess(true);
        setCanResend(false);
        setCountdown(60);
        
        // Reset countdown
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              setCanResend(true);
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error("Error resending email:", error);
    } finally {
      setResending(false);
      setTimeout(() => setResendSuccess(false), 3000);
    }
  };

  const handleCheckManually = async () => {
    setChecking(true);
    try {
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        if (user.emailVerified) {
          localStorage.setItem("user", JSON.stringify({
            email: user.email,
            uid: user.uid
          }));
          window.location.href = "/";
        }
      }
    } catch (error) {
      console.error("Error checking verification:", error);
    }
    setChecking(false);
  };

  const goToSignIn = () => {
    window.location.href = "/signin";
  };

  return (
    <div style={styles.container}>
      <style>{keyframes}</style>
      
      <div style={styles.card}>
        {/* Animated Check Icon */}
        <div style={styles.iconWrapper}>
          <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
            <circle cx="50" cy="50" r="48" stroke="#10B981" strokeWidth="4"/>
            <path d="M30 50L42 62L70 34" stroke="#10B981" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {/* Title */}
        <h1 style={styles.title}>Check Your Email</h1>
        <p style={styles.subtitle}>
          We've sent a verification link to
        </p>
        <p style={styles.email}>{email || "your email address"}</p>
        <p style={styles.description}>
          Click the link in the email to verify your account and get started.
        </p>

        {/* Success message */}
        {resendSuccess && (
          <div style={styles.successBanner}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#10B981" strokeWidth="2"/>
              <path d="M9 12l2 2 4-4" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={styles.successText}>Verification email sent successfully!</span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={styles.buttonGroup}>
          <button 
            style={{
              ...styles.secondaryButton,
              ...((!canResend || resending) ? styles.buttonDisabled : {})
            }}
            onClick={handleResendEmail}
            disabled={!canResend || resending}
            onMouseEnter={(e) => {
              if (canResend && !resending) {
                e.currentTarget.style.background = '#F9FAFB';
                e.currentTarget.style.borderColor = '#8B5CF6';
              }
            }}
            onMouseLeave={(e) => {
              if (canResend && !resending) {
                e.currentTarget.style.background = '#FFFFFF';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }
            }}
          >
            {resending ? (
              <>
                <span style={styles.spinner}></span>
                Sending...
              </>
            ) : canResend ? (
              "Resend Verification Email"
            ) : (
              `Resend in ${countdown}s`
            )}
          </button>
        </div>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine}></div>
        </div>

        {/* Footer */}
        <p style={styles.footerText}>
          Didn't receive the email? Check your spam folder.
        </p>
        <p style={{...styles.footerText, marginTop: '16px'}}>
          Wrong email?{" "}
          <span 
            style={styles.link} 
            onClick={goToSignIn}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Go back to sign in
          </span>
        </p>
      </div>
    </div>
  );
}

const keyframes = `
  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 40px rgba(139, 92, 246, 0.15),
                  0 0 80px rgba(139, 92, 246, 0.1),
                  0 20px 60px rgba(0, 0, 0, 0.08);
    }
    50% {
      box-shadow: 0 0 60px rgba(139, 92, 246, 0.25),
                  0 0 120px rgba(139, 92, 246, 0.15),
                  0 25px 70px rgba(0, 0, 0, 0.1);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 480px) {
    body {
      font-size: 14px;
    }
  }
`;

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    padding: "20px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },

  card: {
    width: "100%",
    maxWidth: "480px",
    padding: "48px 40px",
    background: "#ffffff",
    borderRadius: "24px",
    border: "1px solid rgba(139, 92, 246, 0.08)",
    textAlign: "center",
    animation: "glow 4s ease-in-out infinite, fadeIn 0.6s ease-out",
    position: "relative",
    boxSizing: "border-box",
  },

  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "32px",
    animation: "float 3s ease-in-out infinite",
  },

  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1a1a2e",
    margin: "0 0 12px 0",
    letterSpacing: "-0.5px",
    lineHeight: "1.2",
  },

  subtitle: {
    margin: "0",
    color: "#6B7280",
    fontSize: "15px",
    fontWeight: "400",
    lineHeight: "1.5",
  },

  email: {
    margin: "8px 0 16px 0",
    color: "#8B5CF6",
    fontSize: "16px",
    fontWeight: "600",
    wordBreak: "break-word",
  },

  description: {
    margin: "0 0 32px 0",
    color: "#9CA3AF",
    fontSize: "14px",
    lineHeight: "1.6",
    maxWidth: "380px",
    marginLeft: "auto",
    marginRight: "auto",
  },

  successBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "12px 16px",
    background: "#D1FAE5",
    border: "1px solid #6EE7B7",
    borderRadius: "12px",
    marginBottom: "24px",
    animation: "slideDown 0.3s ease-out",
  },

  successText: {
    fontSize: "14px",
    color: "#047857",
    fontWeight: "500",
  },

  buttonGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "32px",
  },

  primaryButton: {
    width: "100%",
    padding: "16px 24px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
    color: "#ffffff",
    fontWeight: "600",
    fontSize: "16px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 8px 30px rgba(139, 92, 246, 0.3)",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  secondaryButton: {
    width: "100%",
    padding: "16px 24px",
    borderRadius: "12px",
    background: "#FFFFFF",
    color: "#6B7280",
    fontWeight: "600",
    fontSize: "15px",
    border: "2px solid #E5E7EB",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTop: "2px solid #ffffff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    display: "inline-block",
  },

  divider: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    margin: "32px 0",
  },

  dividerLine: {
    flex: 1,
    height: "1px",
    background: "linear-gradient(90deg, transparent, #e5e7eb, transparent)",
  },

  dividerText: {
    fontSize: "13px",
    color: "#9CA3AF",
    fontWeight: "500",
    textTransform: "lowercase",
  },

  footerText: {
    margin: "0",
    fontSize: "14px",
    color: "#6B7280",
    fontWeight: "400",
    lineHeight: "1.6",
  },

  link: {
    color: "#8B5CF6",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textDecoration: "none",
  },
};